import { useMutation } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import React, {
  createContext,
  FormEvent,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState
} from 'react'
import { ElasticSearchResponse, GeneralDocument } from '../models'
import searchService from '../services/search.service'
import { useDebounce } from 'use-debounce'
export type SearchContextType = {
  conversationId?: string
  searchResultData?: ElasticSearchResponse<GeneralDocument>
  searchData: ElasticSearchResponse<GeneralDocument> | null
  handleSubmit: (e: FormEvent) => void
  value: string
  searchValue?: string
  setSearchValue: (value: string) => void
  handlePagination: (page: number) => void
  page: number
  loading: boolean
}

export const SearchContext = createContext<SearchContextType | null>(null)

const SearchContextProvider = ({ children }: PropsWithChildren) => {
  const [searchValue, setSearchValue] = React.useState('')
  const [value] = useDebounce(searchValue, 500)
  const [page, setPage] = useState(1)

  const [searchData, setSearchData] = useState<ElasticSearchResponse<GeneralDocument> | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  // const [error, setError] = useState<string | null>(null)

  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('q')

  const { data, mutate } = useMutation<
    AxiosResponse<ElasticSearchResponse<GeneralDocument>>,
    unknown,
    { searchValue: string; page?: number }
  >({
    mutationKey: ['general-search'],
    mutationFn: async (searchTerm: { searchValue: string; page?: number }) => {
      return searchService.search(searchTerm.searchValue.trim(), searchTerm?.page)
    },
    onMutate: () => {
      // Set loading to true when the mutation starts
      setLoading(true)
    },
    onSuccess: () => {
      // Set loading to false when the mutation succeeds
      setLoading(false)
    },
    onError: (error) => {
      setLoading(false)
      console.log(error)
    }
  })

  const fetchSearchData = async () => {
    if (!searchValue.trim() || value.length <= 2) {
      setSearchData(null) // Reset data if searchValue is empty or too short
      return
    }

    // setLoading(true)
    // setError(null)

    try {
      const result = await searchService.search(searchValue.trim(), 1, 10, true)
      setSearchData(result?.data || null) // Save result to state
    } catch (err) {
      console.error(err) // Log the error for debugging
      // setError('Failed to fetch search results.') // Handle error for user
    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const q = urlParams.get('q')
    const page = urlParams.get('page')
    if (q) {
      mutate({ searchValue: q, page: page ? parseInt(page) : 1 })
      setSearchValue(q)
      setPage(page ? parseInt(page) : 1)
    }
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const q = urlParams.get('q')
    if (q) mutate({ searchValue: q || '', page })
  }, [page])

  useEffect(() => {
    //For suggustion data in search header if input value change
    fetchSearchData()
  }, [value])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    mutate({ searchValue })
  }

  const handlePagination = (page: number) => {
    setPage(page)
    const url = new URL(window.location.href)
    url.searchParams.set('page', `${page}`)
    history.pushState({}, '', url)
  }

  const searchResultData = useMemo(() => {
    return data?.data
  }, [data?.data?.hits])

  return (
    <SearchContext.Provider
      value={{
        searchResultData: searchResultData,
        loading: loading,
        searchData:
          searchValue === queryParam
            ? searchResultData && searchResultData !== null
              ? searchResultData
              : null
            : searchData && searchData !== null
              ? searchData
              : null,

        handleSubmit,
        value,
        searchValue,
        setSearchValue,
        handlePagination,
        page
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export default SearchContextProvider

export const useSearchContext = () => React.useContext(SearchContext) as SearchContextType
